import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Settings, Check, X } from 'lucide-react';
import { consentApi } from '../lib/api';

const STORAGE_KEY = 'reframes_consent';

export const getConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch { return null; }
};

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [choices, setChoices] = useState({});

  useEffect(() => {
    const existing = getConsent();
    if (existing) return; // Already consented

    // Use cached geo data to avoid slow external API call
    const GEO_CACHE_KEY = 'reframes_geo_cache';
    const cached = sessionStorage.getItem(GEO_CACHE_KEY);
    if (cached) {
      try {
        const data = JSON.parse(cached);
        setGeoData(data);
        const initial = {};
        (data.cookie_categories || []).forEach(c => {
          initial[c.key] = c.mandatory ? true : c.default_enabled;
        });
        setChoices(initial);
        setVisible(true);
        return;
      } catch {}
    }

    consentApi.geolocate().then(data => {
      sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(data));
      setGeoData(data);
      const initial = {};
      (data.cookie_categories || []).forEach(c => {
        initial[c.key] = c.mandatory ? true : c.default_enabled;
      });
      setChoices(initial);
      setVisible(true);
    }).catch(() => setVisible(false));
  }, []);

  const saveConsent = useCallback(async (finalChoices) => {
    const record = {
      anonymous_id: `anon_${Date.now().toString(36)}`,
      country_code: geoData?.country_code,
      zone: geoData?.zone,
      consent_model: geoData?.consent_model,
      choices: finalChoices,
      consent_version: '1.0',
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...record, timestamp: new Date().toISOString() }));
    try { await consentApi.record(record); } catch {}
    setVisible(false);
  }, [geoData]);

  const acceptAll = () => {
    const all = {};
    (geoData?.cookie_categories || []).forEach(c => { all[c.key] = true; });
    saveConsent(all);
  };

  const rejectOptional = () => {
    const min = {};
    (geoData?.cookie_categories || []).forEach(c => { min[c.key] = c.mandatory; });
    saveConsent(min);
  };

  const savePrefs = () => saveConsent(choices);

  if (!visible || !geoData) return null;

  return (
    <>
      {/* Banner */}
      {!showPrefs && (
        <div className="fixed bottom-0 inset-x-0 z-[9999] p-2 sm:p-4" data-testid="cookie-banner">
          <div className="max-w-3xl mx-auto bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-xl sm:rounded-2xl p-3 sm:p-5 shadow-2xl shadow-black/50">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center shrink-0 mt-0.5">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-[#8B5CF6]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-xs sm:text-sm mb-0.5 sm:mb-1">We value your privacy</h3>
                <p className="text-[10px] sm:text-xs text-gray-400 leading-relaxed hidden sm:block">
                  We use cookies to enhance your experience.
                  {geoData.consent_model === 'opt-in'
                    ? ' We need your consent before enabling optional cookies.'
                    : ' Optional cookies are enabled by default. You can change your preferences anytime.'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-4 justify-end">
              <button onClick={() => setShowPrefs(true)}
                className="px-4 py-2 text-xs text-gray-300 hover:text-white border border-white/10 rounded-lg transition-colors"
                data-testid="cookie-customize-btn">
                <Settings className="w-3 h-3 inline mr-1.5" />Customize
              </button>
              {geoData.consent_model === 'opt-in' && (
                <button onClick={rejectOptional}
                  className="px-4 py-2 text-xs bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="cookie-reject-btn">
                  Reject Optional
                </button>
              )}
              <button onClick={acceptAll}
                className="px-5 py-2 text-xs bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-lg font-medium transition-colors"
                data-testid="cookie-accept-btn">
                <Check className="w-3 h-3 inline mr-1.5" />Accept All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Panel */}
      {showPrefs && (
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="cookie-prefs-modal">
          <div className="bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-5 border-b border-white/5 flex items-center justify-between">
              <h3 className="font-semibold">Cookie Preferences</h3>
              <button onClick={() => setShowPrefs(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-400 mb-3">
                Region: <span className="text-white font-medium">{geoData.zone_name}</span> ({geoData.country_code}) —
                Model: <span className="text-[#8B5CF6] font-medium">{geoData.consent_model}</span>
              </p>
              {(geoData.cookie_categories || []).map(cat => (
                <div key={cat.key} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
                  <div>
                    <p className="text-sm font-medium">{cat.label}</p>
                    <p className="text-[11px] text-gray-500">
                      {cat.mandatory ? 'Always active — required for the site to function' : 'Optional — you can enable or disable this'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={!!choices[cat.key]} disabled={cat.mandatory}
                      onChange={() => !cat.mandatory && setChoices(p => ({ ...p, [cat.key]: !p[cat.key] }))}
                      className="sr-only peer" data-testid={`cookie-toggle-${cat.key}`} />
                    <div className={`w-9 h-5 rounded-full transition-colors ${cat.mandatory ? 'bg-[#00FF9D]/40' : choices[cat.key] ? 'bg-[#8B5CF6]' : 'bg-gray-600'} peer-focus:ring-2 peer-focus:ring-[#8B5CF6]/30`}>
                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${choices[cat.key] ? 'translate-x-4' : ''}`} />
                    </div>
                  </label>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-white/5 flex gap-2 justify-end">
              <button onClick={rejectOptional}
                className="px-4 py-2 text-xs border border-white/10 rounded-lg hover:bg-white/5"
                data-testid="prefs-reject-btn">
                Reject Optional
              </button>
              <button onClick={savePrefs}
                className="px-5 py-2 text-xs bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-lg font-medium"
                data-testid="prefs-save-btn">
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentBanner;

/* Re-open preferences — call from footer or settings */
export const openCookiePreferences = () => {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event('reframes-reopen-consent'));
};
